/**
 * What a support agent can actually reach, and what this console cannot yet draw.
 *
 * The mechanism is the dispatch, tenant admin and finance consoles', deliberately re-stated rather
 * than imported — see `docs/adr/0021-two-consoles-and-no-shared-shell.md` for why nothing is shared
 * between the channel applications yet. The three questions it answers are the same three:
 *
 *   - visible   — show it in the rail
 *   - reachable — allow it to be opened
 *   - readOnly  — open it, but do not offer the commands
 *
 * What is different here is the history. `docs/design/CONSOLE_SCREEN_SUPPORT.md` recorded this
 * console as **0 of 6**, and its first-blocked-on column for four of those six has since stopped
 * being true. Three decisions moved it, none of them taken for this console:
 *
 *   - **ADR 0078** published `GET /v1/tenants/{tenantId}/support-cases`, the by-identifier read
 *     beside it, and `GET /v1/tenants/{tenantId}/support-cases/{caseId}/timeline`. The sweep's
 *     sentence for `Case queue` was "every support-case operation is `/internal/v1`"; it is not.
 *   - **ADR 0027** published `GET /v1/refunds`, which was the first of four absences on
 *     `Goodwill and refunds`.
 *   - `GET /v1/customer-profiles` answers a collection, which is what `Customer lookup` needs to
 *     resolve anything at all.
 *
 * So four of the six are built and two are not, and **the two that are not are blocked on a domain
 * model rather than on a route**. `Replies and macros` cites SUP-04, which is the customer-360 read
 * model and holds no macro; `Satisfaction` cites SUP-05, which is the escalation service and holds
 * no rating. Neither is route work and neither should be read as one.
 *
 * **Built is not the same as whole.** Each of the four carries at least one designed element with
 * no source anywhere on the platform — an SLA timer, a masked phone number, a refund's case link —
 * and each is a named fidelity defect under CLAUDE.md section 1, recorded in the component that
 * draws it, never invented. The rule is the one the finance console already follows: draw what is
 * published, say what is missing, and never dress an identifier as a name.
 *
 * Nothing here talks to a service. Scopes arrive in the access token, which is the only authority
 * on what an agent may do, and this module decides only what to draw. Every command still goes to
 * a service that checks again.
 */

export interface PageDefinition {
  readonly id: string;
  /** The page's own title, as the design's `PAGES` table writes it. */
  readonly title: string;
  /**
   * The rail's label, where the design's menu uses a shorter one than the page title.
   *
   * One of the six differs: `NAV` says `Lookup` where the page is titled `Customer lookup`. Both
   * are the design's own words, so both are kept rather than one being chosen.
   */
  readonly railLabel?: string;
  /** The design's `NAV` group this item sits in, in that file's own words. */
  readonly section: string;
  /** Read requirements, OpenAPI's shape: any of these alternatives, all scopes within one. */
  readonly readRequirements: readonly (readonly string[])[];
  readonly writeRequirements?: readonly (readonly string[])[];
  /** Why the page exists, in the agent's terms — the design's own `PAGES` description. */
  readonly purpose: string;
  /** Why this page cannot be built yet, if it cannot. */
  readonly blockedBy?: string;
  /** The services this page reads from, by their canonical identifiers. */
  readonly owners: readonly string[];
  /** The Material Symbols Rounded glyph, read from `NAV` in the design file. */
  readonly glyph: string;
}

export type PageAccess = "full" | "read_only" | "forbidden";

export interface ResolvedPage {
  readonly page: PageDefinition;
  readonly access: PageAccess;
  readonly visible: boolean;
}

/**
 * The support console's pages, in the design file's own order and groups.
 *
 * The design's `NAV` is two groups: `Work` — cases, lookup, timeline — and `Tools` — macros,
 * refunds, csat. Kept in that order, so the rail reads the way the design's does.
 */
export const supportPages: readonly PageDefinition[] = [
  {
    id: "cases",
    glyph: "inbox",
    owners: ["SUP-01"],
    title: "Case queue",
    section: "Work",
    purpose: "Open cases sorted by urgency and time to breach, with who owns each one.",
    /*
     * Built, on the listing ADR 0078 published.
     *
     * SUP-01 publishes `id`, `customerReference`, `subject`, `category`, `priority`, `status`,
     * `createdAt` and `updatedAt` for each case, which is every column the design's grid draws —
     * `Case`, `Customer` and `Topic` — plus the priority the design sorts by.
     *
     * **Two designed elements have no source and are recorded rather than invented.** The design's
     * state badges read `reply due 12 min`, `breached 6 min` and `caution · SLA`, and **nothing on
     * any contract publishes a reply deadline**: not a `dueAt`, not a `slaBreachesAt`, not a
     * remaining duration. A timer counting down to a deadline this console chose would be a number
     * an agent acts on and the platform does not hold. And the design's customer column reads
     * `Amina K. · +254 7•• ••• 421`; `customerReference` is a reference, not a name and not a
     * phone, so the column draws the reference in mono and says so. Both are in `CaseQueue.tsx`.
     *
     * The case-assignment service `SCREEN_SPEC.md` cites beside SUP-01 is deliberately not an
     * owner here. Its reads are all `/internal/v1` and the design's `assigned · J. Wekesa` has no
     * public source, so the board does not draw an owner. Its code is **described rather than
     * spelled**, because `tests/service-codes.test.ts` holds every code in this file to being a
     * service the page actually reads from — and a code in a comment is a claim that test cannot
     * tell from an owner.
     */
    /*
     * Both, and `support-cases:agent` is the one that makes this a queue rather than an inbox.
     *
     * Without it SUP-01 narrows the listing to `customer:${actorSubject}` — the reader's **own**
     * cases. A support agent has no cases of their own, so this board could only ever be empty for
     * the people it is built for. It is named `Case queue`, and a queue of one's own support
     * requests is not one.
     *
     * It was deliberately not asked for here, and the reason was sound at the time: the scope was
     * enforced only through a `scopes.has` helper, so the extractor never saw it, so it was in no
     * contract and no realm and could not be granted to anybody. Asking for an ungrantable scope
     * would have turned an empty board into a forbidden one. SUP-01's two routes that genuinely
     * require it now say so with `requireScope`, so it is published and this can ask for it.
     *
     * Required rather than optional: a reader without it is shown a forbidden state naming the
     * scope, which is true and actionable, instead of an empty queue that looks like a quiet day.
     *
     * The three writes SUP-01 publishes are all `/internal/v1`, so the design's `New case` form
     * still has nowhere to send a case from a browser. That button stays drawn and refused, with
     * the reason on it.
     */
    readRequirements: [["support-cases:read", "support-cases:agent"]],
  },
  {
    id: "lookup",
    glyph: "person_search",
    owners: ["CUS-01", "TRP-01"],
    title: "Customer lookup",
    railLabel: "Lookup",
    section: "Work",
    purpose:
      "Find a customer, driver, trip, order or payment by any ID or phone number. Opening a record is audited.",
    /*
     * Built, across two listings, and narrower than the design.
     *
     * CUS-01's `GET /v1/customer-profiles` answers a page of the tenant's customers with
     * `displayName` and `status`; TRP-01's `GET /v1/tenants/{tenantId}/trips` answers a page of
     * trips. Between them the `Record` and `Type` columns have a source for two of the five record
     * kinds the design's chips offer.
     *
     * **What has no source is the search itself.** The design's field takes "ID, phone or email"
     * and nothing on the platform resolves a phone number or an email address to a record — the
     * absence `docs/design/DESIGN_QUESTIONS.md` already records against this screen. So the field
     * filters the page it has rather than querying one it cannot, and says which it is doing. The
     * `Last activity` column and the `Masked fields revealed` figure have no source either; both
     * are recorded in `CustomerLookup.tsx`.
     *
     * Two chips — `order` and `payment` — are drawn disabled, because no listing exists for
     * either from this console's tenant context alone.
     */
    readRequirements: [
      ["customer-profile:read", "customer-profile:roster"],
      /*
       * **Both, and the second was missing until 18 October.**
       *
       * `GET /v1/tenants/{tenantId}/trips` calls `requireScope` twice — `trip:read`, then
       * `trip:workload:read` — and a page that declares only the first is granted only the
       * first, because ADR 0032 derives each client's scopes from what its own screens say
       * they spend. So the live support console signed in, drew this page, and took a 403
       * `insufficient_scope` on the trips half of the lookup while the customer half
       * answered 200.
       *
       * `dispatch · Active rides` reads the same operation and has always declared both. The
       * disagreement between two pages over one operation is what named this, and it is the
       * check worth having: a page's declaration is a claim about a route, and the route is
       * the thing that settles it.
       */
      ["trip:read", "trip:workload:read"],
    ],
  },
  {
    id: "timeline",
    glyph: "timeline",
    owners: ["SUP-03"],
    title: "Case timeline",
    section: "Work",
    purpose: "Everything that happened on case cs_4471, oldest first, across every system.",
    /*
     * Built, and the closest match in this console between what is drawn and what is published.
     *
     * SUP-03 publishes `occurredAt`, `source`, `summary`, `category` and `reference` per entry,
     * which is exactly the design's `When`, `Source` and `Event` columns and the badge beside
     * them. It also publishes `checkpoints` with `lastSequence` per source, which is what lets the
     * page say a system's rows may be behind rather than presenting a partial timeline as whole —
     * the `stale` state CLAUDE.md section 1 requires be built.
     *
     * **The primary action has nowhere to go.** The design's `Add note or reply` form posts a
     * message, and no reply operation exists anywhere on the platform: SUP-03's two writes are
     * `rebuild` and `project`, both of which are a projection's own business and neither of which
     * is an agent writing to a customer. The button is drawn and refused, with the reason on it.
     *
     * The timeline needs a case, which the queue supplies. Opened without one it draws the empty
     * state the design draws, not an error.
     */
    readRequirements: [["support-timeline:read"]],
  },
  {
    id: "macros",
    glyph: "quickreply",
    /*
     * NTF-07, not the design's `SUP-04`. That service is the customer-360 read model and holds no
     * saved reply, which this page's own recorded reason said correctly; what it reads from is the
     * notification template service. R-17's rule applied to a citation.
     */
    owners: ["NTF-07"],
    title: "Replies and macros",
    section: "Tools",
    purpose: "Saved replies agents can send, with variables filled from the case.",
    /*
     * **Served, and the reason that stood here was wrong on three of its four counts.** It read:
     * "Nothing on the platform models a reply macro: no name, no topic, no body in any language.
     * SUP-04, which SCREEN_SPEC.md cites, is the customer-360 read model — it links a subject to a
     * case and holds no saved reply. This is a service that does not exist, not a route that is not
     * published."
     *
     * The sentence about SUP-04 is exactly right and is why this is worth reading twice: that
     * service holds case links and nothing like a saved reply. **But a saved reply with variables,
     * in a locale, with a publication status, is what NTF-07 holds** — `notification_templates`
     * carries a key, a locale, a channel, allowed variables, a body and a
     * `draft | published | retired` status — and ADR 0043 published a summary listing of it weeks
     * ago. So the name, the body and the languages all exist; only the topic does not.
     *
     * [ADR 0088](../../../docs/adr/0088-a-support-macro-is-a-notification-template-this-platform-does-not-distinguish.md)
     * records the measurement and what the one honest fix would be.
     *
     * **The scope is NTF-07's, not an invented one.** `support-macros:read` named a service that
     * does not exist; `template:summary:read` is the scope ADR 0043 published and the tenant admin
     * console already spends.
     */
    readRequirements: [["template:summary:read"]],
  },
  {
    id: "refunds",
    glyph: "undo",
    owners: ["FIN-04"],
    title: "Goodwill and refunds",
    section: "Tools",
    purpose: "Refunds and credits raised from cases. Over the agent limit goes to a supervisor.",
    /*
     * Built, on the listing ADR 0027 published, and it draws two of the design's three columns.
     *
     * FIN-04 publishes `refundId`, `amountMinor`, `currency`, `status`, `reasonCode` and
     * `requestedAt`, so `Request` and `Amount` have a source — and the money is shown the way
     * CLAUDE.md section 2 requires, with the currency code and full minor units.
     *
     * **The `Case` column has no source.** Nothing on FIN-04, or on any contract, links a refund
     * to the case it was raised from: there is no `caseId`, no `sourceReference`, no
     * `raisedFromCase`. The design draws `cs_4455 · fare dispute`. The column draws the refund's
     * own `reasonCode` and `paymentIntentId` and says the case link is not published, because a
     * case identifier this console inferred would be a link an agent clicks.
     *
     * The design's `Awaiting supervisor` state and its `cannot approve a refund they raised` rule
     * have no source either: FIN-04's status enum has no approval state and nothing publishes who
     * raised a refund. Both recorded in `GoodwillRefunds.tsx`.
     */
    readRequirements: [["refund:read"]],
    /*
     * `refund:request` is what the design's `Raise refund` form would spend, and it is deliberately
     * not asked for. The form cannot be completed from here anyway — `POST /v1/refunds` takes a
     * payment intent, which this console has no way to discover from a case — so holding the scope
     * would buy a console the permission to move money and no screen that spends it.
     */
  },
  {
    id: "csat",
    glyph: "sentiment_satisfied",
    /*
     * **SUP-01 and IAM-02, not the design's `SUP-05` — and the correction is the opposite of the
     * one `macros` needed.** There the recorded reason had the service right and the absence wrong;
     * here it had both right, and the service it named was right to be ruled out.
     *
     * The entry that stood here read:
     *
     * > Nothing on the platform models support satisfaction: no rating, no response, no reopen
     * > count. SUP-05, which SCREEN_SPEC.md cites, is the support escalation service — it holds
     * > policies, timers and escalations, and every one of its operations is /internal/v1. Both
     * > halves would have to be answered, and the first one is a service that does not exist.
     *
     * ADR 0088 had just shown that a correct citation can sit beside three wrong claims, so the
     * question it asked — does anything **else** hold this — was asked here before a table was
     * written. **Nothing did.** RAT-01 is the only rating service on the platform, its
     * `transactionType` is a one-member enum (`TRIP`), and it publishes a submit, a read by
     * identifier and no listing at all. So this is the second record in this plan that genuinely did
     * not exist, after ADR 0084's staff member, and ADR 0090 adds it to SUP-01 — the service that
     * owns the case, where the design's own precondition is already a row.
     *
     * IAM-02 is the second owner because the first column is its field: SUP-01 holds a platform
     * subject and no names, and ADR 0084's member listing is where `J. Wekesa` comes from.
     *
     * **Still degraded.** None of the four tiles is drawn and the design's `top` and `coaching`
     * badges are a judgement against a threshold nothing publishes, so no row claims either.
     */
    owners: ["SUP-01", "IAM-02"],
    title: "Satisfaction",
    section: "Tools",
    purpose: "How customers rated their support, and cases reopened.",
    readRequirements: [["support-satisfaction:read"]],
  },
] as const;

/**
 * Whether the granted scopes satisfy any one of the alternatives outright.
 *
 * `every` inside `some`: a partly-held requirement is not partial access to a route, it is a
 * refusal.
 */
function satisfies(
  granted: ReadonlySet<string>,
  requirements: readonly (readonly string[])[],
): boolean {
  return requirements.some((alternative) => alternative.every((scope) => granted.has(scope)));
}

export function resolvePage(page: PageDefinition, granted: ReadonlySet<string>): ResolvedPage {
  // A page the platform cannot support is forbidden to everyone, including an agent holding every
  // scope in existence. On this console that is two of six, which is why the rail's foot has
  // something to say.
  if (page.blockedBy !== undefined) return { page, access: "forbidden", visible: false };

  const canRead = satisfies(granted, page.readRequirements);
  const canWrite =
    page.writeRequirements !== undefined && satisfies(granted, page.writeRequirements);

  if (!canRead && canWrite) return { page, access: "full", visible: false };
  if (!canRead) return { page, access: "forbidden", visible: false };
  return { page, access: canWrite ? "full" : "read_only", visible: true };
}

export interface NavigationSection {
  readonly section: string;
  readonly pages: readonly ResolvedPage[];
}

/** Navigation for one agent, grouped as the design groups it, with empty groups dropped. */
export function buildNavigation(
  granted: ReadonlySet<string>,
  pages: readonly PageDefinition[] = supportPages,
): NavigationSection[] {
  const sections: NavigationSection[] = [];
  for (const page of pages) {
    const resolved = resolvePage(page, granted);
    if (!resolved.visible) continue;
    const existing = sections.find((candidate) => candidate.section === page.section);
    if (existing === undefined) sections.push({ section: page.section, pages: [resolved] });
    else (existing.pages as ResolvedPage[]).push(resolved);
  }
  return sections;
}

/**
 * Whether an agent can open a page they navigated to directly.
 *
 * Separate from visibility on purpose: a bookmark, a link in an escalation notification and a deep
 * link from a case all arrive without passing through the rail.
 */
export function canOpen(pageId: string, granted: ReadonlySet<string>): boolean {
  const page = supportPages.find((candidate) => candidate.id === pageId);
  if (page === undefined) return false;
  return resolvePage(page, granted).access !== "forbidden";
}

/** Every scope this console can make use of, for a token request or an audit. */
export function allConsoleScopes(pages: readonly PageDefinition[] = supportPages): string[] {
  const scopes = new Set<string>();
  for (const page of pages) {
    if (page.blockedBy !== undefined) continue;
    for (const scope of page.readRequirements.flat()) scopes.add(scope);
    for (const scope of (page.writeRequirements ?? []).flat()) scopes.add(scope);
  }
  return [...scopes].sort();
}

/**
 * How many pages this agent cannot see, and what each would need.
 *
 * Only pages hidden by scope. A page the platform cannot support is invisible to everybody,
 * including an agent holding every scope, so counting it here would tell them their role is short
 * of something no role has — and on this console it would say two.
 */
export function pagesNotInRole(
  granted: ReadonlySet<string>,
  pages: readonly PageDefinition[] = supportPages,
): { id: string; title: string; needs: readonly string[] }[] {
  return pages
    .filter((page) => page.blockedBy === undefined && !resolvePage(page, granted).visible)
    .map((page) => ({
      id: page.id,
      title: page.title,
      // The cheapest way in: the alternative with fewest scopes not already held.
      needs:
        [...page.readRequirements]
          .map((alternative) => alternative.filter((scope) => !granted.has(scope)))
          .sort((left, right) => left.length - right.length)[0] ?? [],
    }));
}

/** The pages the platform cannot support yet, and why. */
export function blockedPages(
  pages: readonly PageDefinition[] = supportPages,
): { id: string; title: string; blockedBy: string }[] {
  return pages
    .filter((page): page is PageDefinition & { blockedBy: string } => page.blockedBy !== undefined)
    .map((page) => ({ id: page.id, title: page.title, blockedBy: page.blockedBy }));
}
